import * as React from "react";

import { ActionIcon, Group, Tooltip } from "@mantine/core";
import { IconBold } from "@tabler/icons-react";

const fontWeights = [400, 600, 700, 800] as const;
export type FontWeight = (typeof fontWeights)[number];

export function isFontWeight(weight: number | undefined): weight is FontWeight {
  return fontWeights.includes(weight as FontWeight);
}

export const FontWeightSelector = ({
  selected,
  defaultWeight,
  onClear,
  onSelect,
}: {
  selected?: number | undefined;
  defaultWeight?: number;
  onClear: () => void;
  onSelect: (newWeight: FontWeight) => void;
}): JSX.Element => {
  const selectedWeight = isFontWeight(selected) ? selected : undefined;
  const is400Selected =
    (selectedWeight === undefined && defaultWeight === 400) || selectedWeight === 400;
  const is600Selected =
    (selectedWeight === undefined && defaultWeight === 600) || selectedWeight === 600;
  const is700Selected =
    (selectedWeight === undefined && defaultWeight === 700) || selectedWeight === 700;
  const is800Selected =
    (selectedWeight === undefined && defaultWeight === 800) || selectedWeight === 800;

  return (
    <Group spacing={1}>
      <Tooltip withArrow label="Normal">
        <ActionIcon
          size="sm"
          variant={is400Selected ? "filled" : "outline"}
          color="dark"
          onClick={() => {
            if (defaultWeight === 400) {
              onClear();
            } else {
              onSelect(400);
            }
          }}
        >
          <IconBold size={14} stroke={1.5} />
        </ActionIcon>
      </Tooltip>
      <Tooltip withArrow label="Semi-bold">
        <ActionIcon
          size="sm"
          variant={is600Selected ? "filled" : "outline"}
          color="dark"
          onClick={() => {
            if (defaultWeight === 600) {
              onClear();
            } else {
              onSelect(600);
            }
          }}
        >
          <IconBold size={14} stroke={2} />
        </ActionIcon>
      </Tooltip>
      <Tooltip withArrow label="Bold">
        <ActionIcon
          size="sm"
          variant={is700Selected ? "filled" : "outline"}
          color="dark"
          onClick={() => {
            if (defaultWeight === 700) {
              onClear();
            } else {
              onSelect(700);
            }
          }}
        >
          <IconBold size={14} stroke={3} />
        </ActionIcon>
      </Tooltip>
      <Tooltip withArrow label="Extra Bold">
        <ActionIcon
          size="sm"
          variant={is800Selected ? "filled" : "outline"}
          color="dark"
          onClick={() => {
            if (defaultWeight === 800) {
              onClear();
            } else {
              onSelect(800);
            }
          }}
        >
          <IconBold size={14} stroke={4} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
};
